import md5 from 'md5';
import { clone, resolvePath } from '../common';
import * as sectionUtils from './sectionUtils';
import { stepHasRequiredData } from '../form/formUtils';
import { updateTrygTealium } from '../tracking';

export const getEmptyState = () => {
  return {
    packageData: {},
    sectionOrder: [],
    currentSectionId: null,
    finishedSections: [],
    finishedPagesData: {},
    nextSection: false,
    currentPageData: {
      pagesOrder: [],
      currentPageId: null,
      finishedPages: [],
      finishedStepsData: {},
      nextPage: false,
      currentStepData: {
        stepsOrder: [],
        currentStepId: null,
        finishedSteps: [],
      },
    },
  };
};

/**
 * Returns the initial state for the section reducer.
 * @return {object} The initial state object.
 */
export const getInitialState = (packageData) => {
  if (!packageData) {
    return getEmptyState();
  }
  let locationHash = '';
  const hasPreselectDefaults = Object.prototype.hasOwnProperty.call(packageData, 'preselectDefaults');
  if (hasPreselectDefaults) {
    const hasStartHash = Object.prototype.hasOwnProperty.call(packageData.preselectDefaults, 'startHash');
    if (hasStartHash) {
      locationHash = packageData.preselectDefaults.startHash.substr(1);
    }
  }
  const { sectionId, pageId, stepId } = sectionUtils.getIdsFromHash(locationHash);
  const isHashValid = !Number.isNaN(sectionId) && !Number.isNaN(pageId) && !Number.isNaN(stepId);
  const processedPackageData = sectionUtils.processPackageData(packageData);

  // The IDs of sections in the order they should appear.
  const sectionOrder = sectionUtils.getSectionOrder(processedPackageData);

  // Initial section will be first ID of the ordered list.
  const validsectionId = sectionOrder.includes(sectionId);
  const currentSectionId = isHashValid && validsectionId ? sectionId : sectionOrder[0];

  // The data of the current section and save it.
  const currentSection = sectionUtils.getSectionById(currentSectionId, processedPackageData);

  // The IDs of pages in the order they should appear.
  const pagesOrder = sectionUtils.getPagesOrder(currentSection);

  // Initial page ID.
  const validPageId = pagesOrder.includes(pageId);
  const currentPageId = isHashValid && validPageId ? pageId : pagesOrder[0];

  // The data of the current page and save it.
  const currentPage = sectionUtils.getPageById(currentPageId, currentSection);

  // The IDs of steps in the current page in order of appearence.
  const stepsOrder = sectionUtils.getStepsOrder(currentPage);

  // Initial page step ID.
  const validStepId = stepsOrder.includes(stepId);
  const currentStepId = isHashValid && validStepId ? stepId : stepsOrder[0];

  const baseClass = packageData.bodyClass;

  const newState = {
    packageData: processedPackageData,
    sectionOrder,
    currentSectionId,
    finishedSections: [],
    finishedPagesData: {},
    nextSection: false,
    currentHash: '',
    currentPageData: {
      pagesOrder,
      currentPageId,
      finishedPages: [],
      finishedStepsData: {},
      nextPage: false,
      currentStepData: {
        stepsOrder,
        currentStepId,
        finishedSteps: [],
      },
    },
  };

  if (baseClass) {
    newState.bodyClass = baseClass;
  }
  if (isHashValid && sectionId !== 0) {
    newState.finishedSections = sectionOrder.slice(0, sectionOrder.indexOf(sectionId));
  }
  newState.currentHash = sectionUtils.getStateHash(newState);
  return newState;
};

/**
 * Gets the state to use while loading the data.
 * @return {object} The state.
 */
export const getRequestState = state => {
  return {
    ...state,
    isFetching: true,
  };
};

/**
 * Gets the state to use when data is loaded.
 * @return {object} The state.
 */
export function getReceiveState(state, action) {
  // Reset the state if there are changes to the data.
  const initState = getInitialState(action.payload);
  const initStateHash = md5(JSON.stringify(initState.packageData));
  const currentStateHash = md5(JSON.stringify(state.packageData));
  const newState = initStateHash === currentStateHash ? state : initState;
  return {
    ...newState,
    isFetching: false,
  };
}

/**
 * Gets the state to use when data is loaded.
 * @param  {Object}  state The current state of section
 * @param  {Object}  payload The data to update
 * @return {object} The updated state.
 */
export function setPackageUpdate(state, payload) {
  // Reset the state if there are changes to the data.
  const { errorData } = payload;
  const newState = clone(state);
  if (errorData) {
    // Reset the original component after error flow component.
    const {
      currentSectionId, currentPageId, original,
    } = errorData;
    const section = sectionUtils.getSectionById(currentSectionId, state.packageData);
    const sectionIndex = sectionUtils.getSectionById(currentSectionId, state.packageData, true);
    const pageIndex = sectionUtils.getPageById(currentPageId, section, true);
    newState.packageData.sections[sectionIndex].pages[pageIndex] = original;
  }
  return {
    ...newState,
  };
}

/**
 * Validates that a step is valid in the current context.
 * @param  {Object}  stepData The data of the step
 * @param  {Object}  userData The current user data.
 * @param  {Object}  isBack   Allow to skip the current step.
 * @return {Boolean}          True indicating it is valid.
 */
const isStepValid = (stepData, userData, isBack = false) => {
  let isValid = true;
  const { showForMissingData } = stepData;

  if (isBack && Object.hasOwnProperty.call(stepData, 'skipBack')) {
    const { skipBack } = stepData;
    if (skipBack === true) {
      return false;
    }
  }

  if (Object.hasOwnProperty.call(stepData, 'showIf')) {
    const { showIf } = stepData;
    isValid = stepHasRequiredData(showIf, userData, {});
    if (!isValid) {
      return isValid;
    }
  }

  if (showForMissingData) {
    // Check to see if data is missing. If it does the step is valid.
    isValid = showForMissingData.reduce((result, item) => {
      const value = resolvePath(userData, item);
      if (value === false || value) {
        // The value exist. Meaning the step is invalid.
        return false;
      }
      return result;
    }, isValid);
  }
  return isValid;
};

/**
 * Finds the next valid step to use in a page.
 * @param  {Object} userData          The current user data.
 * @param  {Object} pageData          The data of the page to find a valid step in.
 * @param  {Array}  stepOrder         The step order of the page.
 * @param  {Number} [currentStepId=0] The current step id.
 * @return {Number|boolean}           The ID of the next step.
 */
const getNextStep = (userData, pageData, stepOrder, currentStepId = 0) => {
  // Find the position of the current step in the ordered array.
  const currentStepIndex = stepOrder.findIndex(stepOrderId => stepOrderId === currentStepId);

  // If there is no next step in this page we return false.
  if (currentStepIndex === stepOrder.length - 1) {
    return false;
  }

  // Loop the steps in this page until we find a valid one.
  const nextStepId = stepOrder.slice(currentStepIndex + 1).find(stepId => {
    const step = sectionUtils.getStepById(stepId, pageData);
    return isStepValid(step, userData);
  });
  return nextStepId;
};

/**
 * Updates the state with the next section.
 * @param  {object} state     The state to update.
 * @param  {object} userData  The current user data.
 * @return {object}           The new updated state.
 */
export const nextSection = (state, userData) => {
  const { currentSectionId, sectionOrder, packageData: { translations } } = state;

  // Find the position of the current section in the ordered array.
  const curSectionIndex = sectionOrder.findIndex(sectionOrderId =>
    sectionOrderId === currentSectionId
  );

  // Add the current page to the finished pages.
  const { finishedSections, currentPageData } = state;
  finishedSections.push(currentSectionId);

  // Save current page data in case we need it for jumping later.
  const finishedPagesData = {
    currentSectionId,
    ...currentPageData,
  };

  // Check if there is a next section.
  const nextSectionId = (sectionOrder.length - 1 > curSectionIndex)
    ? sectionOrder[curSectionIndex + 1]
    : false;

  // If no next section we need to continue in the flow.
  if (nextSectionId === false) {
    // TODO!
    return {};
  }

  // Find the data for the first step in the next page and first page in next section.
  const nextSectionData = sectionUtils.getSectionById(nextSectionId, state.packageData);
  const nextPagesOrder = sectionUtils.getPagesOrder(nextSectionData);
  let nextStepsOrder = [];
  let nextPageId = null;

  // Loop pages until we find a next step or run out of pages.
  const nextStepId = nextPagesOrder.reduce((result, item) => {
    // Return if we already found a next step.
    if (result !== false) {
      return result;
    }
    nextPageId = item;
    const nextPageData = sectionUtils.getPageById(item, nextSectionData);
    nextStepsOrder = sectionUtils.getStepsOrder(nextPageData);
    const newNextStepId = getNextStep(userData, nextPageData, nextStepsOrder);
    // If we did not find any new step return false.
    if (newNextStepId === false || newNextStepId === undefined) {
      return false;
    }
    return newNextStepId;
  }, false);

  // If we still havent found any next step we need to switch section.
  if (nextStepId === false || nextStepId === undefined) {
    const tempState = {
      ...state,
      finishedSections,
      currentSectionId: nextSectionId,
      currentPageData: {},
    };
    return nextSection(tempState, userData);
  }

  const newState = {
    ...state,
    finishedSections,
    finishedPagesData,
    currentSectionId: nextSectionId,
    nextSection: false,
    currentPageData: {
      ...state.currentPageData,
      pagesOrder: nextPagesOrder,
      finishedPages: [],
      finishedStepsData: [],
      currentPageId: nextPageId,
      nextPage: false,
      currentStepData: {
        stepsOrder: nextStepsOrder,
        currentStepId: nextStepId,
        finishedSteps: [],
      },
    },
  };
  newState.currentHash = sectionUtils.getStateHash(newState);

  // Check we need to update tracking data.
  updateTrygTealium(newState.currentHash, userData, translations);
  return newState;
};

/**
 * Updates the state with the next page.
 * If no more pages are found we dispatch a NEXT_SECTION action.
 * @param  {object} state  The state to update.
 * @param  {object} userData  The current user data.
 * @return {object}        The new updated state.
 */
export const nextPage = (state, userData) => {
  const { currentPageData: { currentPageId, pagesOrder }, packageData: { translations } } = state;

  // Find the position of the current page in the ordered array.
  const currentPageIndex = pagesOrder.findIndex(pageOrderId => pageOrderId === currentPageId);

  // Add the current page to the finished pages.
  const { finishedPages, currentStepData } = state.currentPageData;
  finishedPages.push(currentPageId);

  // Save current step data in case we need it for jumping later.
  const finishedStepsData = {
    currentPageId,
    ...currentStepData,
  };

  // Check if there is a next page.
  const nextPageId = (pagesOrder.length - 1 > currentPageIndex) ?
    pagesOrder[currentPageIndex + 1] :
    false;

  // Update the state to handle a section change.
  if (nextPageId === false) {
    return {
      ...state,
      nextSection: true,
      currentPageData: {
        ...state.currentPageData,
        finishedPages,
        finishedStepsData,
      },
    };
  }

  // Find the data for the first step in the next page.
  const { currentSectionId } = state;
  const currentSection = sectionUtils.getSectionById(currentSectionId, state.packageData);
  const nextPageData = sectionUtils.getPageById(nextPageId, currentSection);
  const nextStepsOrder = sectionUtils.getStepsOrder(nextPageData);
  // Check if there is a next step.
  const nextStepId = getNextStep(userData, nextPageData, nextStepsOrder);

  // If page has no valid steps we initiate another page change.
  if (nextStepId === false || nextStepId === undefined) {
    // Create a dummy state for another next page call.
    finishedPages.push(nextPageId);
    const tempState = {
      ...state,
      currentPageData: {
        ...nextPageData,
        pagesOrder,
        finishedPages,
        currentStepData: {},
        currentPageId: nextPageId,
        nextPage: true,
      },
    };
    return nextPage(tempState, userData);
  }

  const newState = {
    ...state,
    currentPageData: {
      ...state.currentPageData,
      finishedPages,
      finishedStepsData,
      currentPageId: nextPageId,
      nextPage: false,
      currentStepData: {
        stepsOrder: nextStepsOrder,
        currentStepId: nextStepId,
        finishedSteps: [],
      },
    },
  };

  newState.currentHash = sectionUtils.getStateHash(newState);
  // Check we need to update tracking data.
  updateTrygTealium(newState.currentHash, userData, translations);
  return newState;
};

/**
 * Updates the state with the next step.
 * If no more steps are found we dispatch a NEXT_PAGE action.
 * @param  {object} state  The state to update.
 * @param  {object} userData  The current user data.
 * @return {object}        The new updated state.
 */
export const nextStep = (state, userData) => {
  const {
    currentSectionId,
    currentPageData: { currentPageId, currentStepData },
    packageData,
    packageData: { translations },
  } = state;
  const { currentStepId, stepsOrder } = currentStepData;

  // Check if there is a next step.
  const sectionData = sectionUtils.getSectionById(currentSectionId, packageData);
  const pageData = sectionUtils.getPageById(currentPageId, sectionData);
  const nextStepId = getNextStep(userData, pageData, stepsOrder, currentStepId);

  // If it's not already added, add the current step to the finished steps.
  const { finishedSteps } = currentStepData;
  if (!finishedSteps.includes(currentStepId)) {
    finishedSteps.push(currentStepId);
  }

  // Update the state to handle a page change.
  if (nextStepId === false || nextStepId === undefined) {
    return {
      ...state,
      currentPageData: {
        ...state.currentPageData,
        nextPage: true,
        currentStepData: {
          ...state.currentPageData.currentStepData,
          finishedSteps,
        },
      },
    };
  }

  const newState = {
    ...state,
    currentPageData: {
      ...state.currentPageData,
      currentStepData: {
        ...state.currentPageData.currentStepData,
        finishedSteps,
        currentStepId: nextStepId,
      },
    },
  };

  newState.currentHash = sectionUtils.getStateHash(newState);
  // Check we need to update tracking data.
  updateTrygTealium(newState.currentHash, userData, translations);
  return newState;
};

/**
 * Updates the state with the next step.
 * If no more steps are found we dispatch a NEXT_PAGE action.
 * @param  {object} action  The action data to update.
 * @param  {object} state   The state to update.
 * @return {object}         The new updated state.
 */
export const elementToggle = (action, state) => {
  const {
    currentSectionId,
    currentPageData: {
      currentPageId,
      currentStepData: {
        currentStepId,
      },
    },
    packageData,
  } = state;

  const sectionData = sectionUtils.getSectionById(currentSectionId, packageData);
  const pageData = sectionUtils.getPageById(currentPageId, sectionData);
  const stepData = sectionUtils.getStepById(currentStepId, pageData);
  const sectionIndex = sectionUtils.getSectionById(currentSectionId, packageData, true);
  const pageIndex = sectionUtils.getPageById(currentPageId, sectionData, true);
  const stepIndex = sectionUtils.getStepById(currentStepId, pageData, true);

  const updatedStepProps = stepData.component.props.map(prop => {
    return prop.id === action.payload
      ? {
        ...prop,
        hidden: !prop.hidden,
      }
      : prop;
  });
  packageData.sections[sectionIndex].pages[pageIndex].steps[stepIndex].component.props =
    updatedStepProps;
  return {
    ...state,
  };
};

export const prevStep = (state, userData) => {
  // Find section, page and step ids we want to return to.
  // If there are no previous ids we keep the current.
  const {
    packageData,
    packageData: {
      translations,
    },
    currentSectionId,
    sectionOrder,
    currentPageData,
    currentPageData: {
      currentPageId,
      currentStepData: {
        currentStepId,
        stepsOrder,
      },
    },
  } = state;

  let {
    currentPageData: {
      pagesOrder,
    },
  } = state;

  // Section.
  let curSectionIndex = sectionOrder.findIndex(id =>
    id === currentSectionId
  );

  // Page.
  let curPageIndex = pagesOrder.findIndex(id =>
    id === currentPageId
  );

  // Step.
  let curStepIndex = stepsOrder.findIndex(id =>
    id === currentStepId
  );

  let nextSectionId = sectionOrder[curSectionIndex];
  let nextPageId = pagesOrder[curPageIndex];
  let nextStepId = stepsOrder[curStepIndex];

  let nextSectionData = sectionUtils.getSectionById(nextSectionId, packageData);
  let nextPagesOrder = sectionUtils.getPagesOrder(nextSectionData);

  let nextPageData = sectionUtils.getPageById(nextPageId, nextSectionData);
  let nextStepsOrder = sectionUtils.getStepsOrder(nextPageData);

  // Check if we need to change page and section further back.
  // Loop everything until we find a valid step.
  let stepFound = false;
  while (!stepFound) {
    if (curStepIndex === 0) {
      if (curPageIndex === 0) {
        // We need to update section too.
        if (curSectionIndex > 0) {
          curSectionIndex--;
          // Get last page and last step of new section.
          nextSectionId = sectionOrder[curSectionIndex];
          nextSectionData = sectionUtils.getSectionById(nextSectionId, packageData);
          nextPagesOrder = sectionUtils.getPagesOrder(nextSectionData);
          nextPageId = nextPagesOrder[nextPagesOrder.length - 1];
          nextPageData = sectionUtils.getPageById(nextPageId, nextSectionData);
          nextStepsOrder = sectionUtils.getStepsOrder(nextPageData);
          nextStepId = nextStepsOrder[nextStepsOrder.length - 1];
          const step = sectionUtils.getStepById(nextStepId, nextPageData);
          stepFound = isStepValid(step, userData, true);
          if (!stepFound && nextPageId > 0) {
            curPageIndex = nextPagesOrder.length - 1;
          }
          pagesOrder = nextPagesOrder;
        }
      } else {
        curPageIndex--;
        // Get last step of new section.
        nextPageId = pagesOrder[curPageIndex];
        nextPageData = sectionUtils.getPageById(nextPageId, nextSectionData);
        nextStepsOrder = sectionUtils.getStepsOrder(nextPageData);
        nextStepId = nextStepsOrder[nextStepsOrder.length - 1];
        const step = sectionUtils.getStepById(nextStepId, nextPageData);
        stepFound = isStepValid(step, userData, true);
      }
    } else {
      curStepIndex--;
      nextStepId = stepsOrder[curStepIndex];
      const step = sectionUtils.getStepById(nextStepId, currentPageData);
      stepFound = isStepValid(step, userData, true);
    }
  }

  const newState = {
    ...state,
    nextSectionData,
    nextPageData,
    currentSectionId: nextSectionId,
    nextSection: false,
    currentPageData: {
      ...state.currentPageData,
      pagesOrder: nextPagesOrder,
      currentPageId: nextPageId,
      nextPage: false,
      currentStepData: {
        ...state.currentPageData.currentStepData,
        stepsOrder: nextStepsOrder,
        currentStepId: nextStepId,
      },
    },
  };

  newState.currentHash = sectionUtils.getStateHash(newState);
  // Check we need to update tracking data.
  updateTrygTealium(newState.currentHash, userData, translations);
  return newState;
};



// WEBPACK FOOTER //
// ./src/utilities/section/sectionReducerUtils.js